import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { apiUtils } from '../utils';

const AdminQueueContext = createContext();

/**
 * AdminQueueProvider - Manages admin queue data and WebSocket subscriptions
 * 
 * This provider handles all admin queue updates through WebSocket subscriptions
 * and provides fallback data fetching when needed.
 */
export function AdminQueueProvider({ children }) {
	const [queueData, setQueueData] = useState({
		activeScans: 0,
		pendingScans: 0,
		completedToday: 0,
		failedToday: 0,
		systemHealth: 'healthy',
		lastUpdated: null,
		isLoading: true,
		error: null,
	});

	/**
	 * Handle WebSocket messages for admin queue updates
	 */
	const handleQueueMessage = useCallback((message) => {
		const { type, payload } = message;

		if (type === 'admin.queue.updated') {
			setQueueData(prev => ({
				...prev,
				...payload,
				lastUpdated: new Date(),
				isLoading: false,
				error: null,
			}));
		} else if (type === 'system.health') {
			setQueueData(prev => ({
				...prev,
				systemHealth: payload.status,
				lastUpdated: new Date(),
				isLoading: false,
				error: null,
			}));
		}
	}, []);

	// Subscribe to admin queue updates
	useSubscription('admin:queue', handleQueueMessage);
	useSubscription('admin:system', handleQueueMessage);

	/**
	 * Fetch admin queue data via HTTP (fallback or initial load)
	 */
	const fetchQueueData = useCallback(async () => {
		try {
			const response = await apiUtils.get('/api/dashboard/admin-stats');

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			setQueueData(prev => ({
				...prev,
				...data,
				lastUpdated: new Date(),
				isLoading: false,
				error: null,
			}));

			return data;
		} catch (error) {
			console.error('Error fetching admin queue data:', error);

			setQueueData(prev => ({
				...prev,
				isLoading: false,
				error: error.message,
			}));

			throw error;
		}
	}, []);

	/**
	 * Refresh queue data manually
	 */
	const refreshQueueData = useCallback(async () => {
		setQueueData(prev => ({ ...prev, isLoading: true, error: null }));
		return await fetchQueueData();
	}, [fetchQueueData]);

	const contextValue = useMemo(() => ({
		queueData,
		fetchQueueData,
		refreshQueueData,
	}), [queueData, fetchQueueData, refreshQueueData]);

	return (
		<AdminQueueContext.Provider value={contextValue}>
			{children}
		</AdminQueueContext.Provider>
	);
}

/**
 * Hook to access admin queue context
 */
export function useAdminQueueContext() {
	const context = useContext(AdminQueueContext);
	if (!context) {
		throw new Error('useAdminQueueContext must be used within an AdminQueueProvider');
	}
	return context;
}

/**
 * Hook for admin queue data management
 */
export function useAdminQueue(options = {}) {
	const { autoRefresh = true, refreshInterval = 10000 } = options;
	const { queueData, fetchQueueData, refreshQueueData } = useAdminQueueContext();

	// Auto-refresh functionality
	React.useEffect(() => {
		if (!autoRefresh) return;

		// Initial fetch
		fetchQueueData();

		// Set up polling as fallback
		const interval = setInterval(() => {
			if (document.hidden) return; // no polling from background tabs
			fetchQueueData();
		}, refreshInterval);

		return () => clearInterval(interval);
	}, [autoRefresh, refreshInterval, fetchQueueData]);

	return {
		queueData,
		isLoading: queueData.isLoading,
		error: queueData.error,
		refreshQueueData,
		lastUpdated: queueData.lastUpdated,
	};
}

export default AdminQueueContext;
